import type { MouseEvent, ReactNode } from 'react';
import { useClassName } from './useClassName';
import type { SxProps } from './sxProps';
import { palette } from './palette';
import { styleRule } from './styleRule';

/**
 * - `primary`: charcoal block for light backgrounds.
 * - `secondary`: charcoal outline for light backgrounds.
 * - `accent`: vermilion block for dark backgrounds.
 * - `inverse`: cream outline for dark backgrounds.
 * - `text`: charcoal label with a vermilion underline bar.
 */
export type ButtonTone = 'primary' | 'secondary' | 'accent' | 'inverse' | 'text';

/** `hero` also slides an arrow in on hover. */
export type ButtonSize = 'small' | 'medium' | 'large' | 'xl' | 'hero';

export type ButtonBaseProps = {
  children?: ReactNode;
  id?: string;
  title?: string;
  'aria-label'?: string;
  onClick?: (event: MouseEvent<HTMLElement>) => void;
};

export type ButtonElementProps =
  | { href: string; target?: string; rel?: string; type?: never; disabled?: never }
  | {
      href?: undefined;
      target?: never;
      rel?: never;
      type?: 'button' | 'submit';
      disabled?: boolean;
    };

export type ButtonProps = ButtonBaseProps &
  ButtonElementProps & {
    tone?: ButtonTone;
    size?: ButtonSize;
    fullWidth?: boolean;
    startIcon?: ReactNode;
    endIcon?: ReactNode;
  };

const baseSx: SxProps = {
  position: 'relative',
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'center',
  gap: '10px',
  boxSizing: 'border-box',
  margin: 0,
  borderRadius: 0,
  fontFamily: 'inherit',
  fontWeight: 600,
  letterSpacing: '0.01em',
  lineHeight: 1.2,
  textAlign: 'center',
  textDecoration: 'none',
  cursor: 'pointer',
  userSelect: 'none',
  verticalAlign: 'middle',
  transition: 'background-color 0.2s, color 0.2s, border-color 0.2s',
  '&:focus-visible': {
    outline: `3px solid ${palette.teal}`,
    outlineOffset: '3px',
  },
  '&:disabled, &[aria-disabled="true"]': {
    cursor: 'default',
    opacity: 0.45,
  },
};

const toneSx: Record<ButtonTone, SxProps> = {
  primary: {
    backgroundColor: palette.charcoal,
    color: palette.cream,
    border: `2px solid ${palette.charcoal}`,
    '&:hover': {
      backgroundColor: palette.vermilion,
      borderColor: palette.vermilion,
    },
  },
  secondary: {
    backgroundColor: 'transparent',
    color: palette.charcoal,
    border: `2px solid ${palette.charcoal}`,
    '&:hover': {
      backgroundColor: palette.charcoal,
      color: palette.cream,
    },
  },
  accent: {
    backgroundColor: palette.vermilion,
    color: palette.cream,
    border: `2px solid ${palette.vermilion}`,
    '&:hover': {
      backgroundColor: palette.teal,
      borderColor: palette.teal,
    },
  },
  inverse: {
    backgroundColor: 'transparent',
    color: palette.cream,
    border: `2px solid ${palette.cream}`,
    '&:hover': {
      backgroundColor: palette.cream,
      color: palette.charcoal,
    },
  },
  text: {
    backgroundColor: 'transparent',
    color: palette.charcoal,
    border: '2px solid transparent',
    textDecoration: 'underline',
    textDecorationColor: palette.vermilion,
    textDecorationThickness: '3px',
    textUnderlineOffset: '8px',
    '&:hover': { color: palette.vermilion },
  },
};

const sizeSx: Record<ButtonSize, SxProps> = {
  small: {
    minHeight: '2.5rem',
    padding: '6px 20px',
    fontSize: '0.95rem',
    whiteSpace: 'nowrap',
    flexShrink: 0,
  },
  medium: { minHeight: '3rem', padding: '8px 28px', fontSize: '1rem' },
  large: {
    minHeight: '3.5rem',
    padding: '10px 40px',
    fontSize: '1.1rem',
    '@media (max-width: 600px)': { padding: '10px 24px' },
  },
  xl: {
    minHeight: '7.5rem',
    padding: '20px 48px',
    fontSize: '2rem',
    fontWeight: 700,
    '@media (max-width: 600px)': { minHeight: '4.5rem', padding: '16px 24px', fontSize: '1.4rem' },
  },
  hero: {
    padding: '24px 32px',
    fontSize: '1.5rem',
    letterSpacing: '-0.66px',
    '@media (max-width: 600px)': { padding: '14px 24px', fontSize: '1.15rem' },
    '& .button-hero-label': {
      display: 'inline-block',
      paddingInline: '0.5rem',
      transition: 'translate 180ms ease-out',
    },
    '& .button-hero-arrow': {
      position: 'absolute',
      inset: '50% -1.125rem auto auto',
      translate: '0 -50%',
      display: 'inline-flex',
      width: '2.25rem',
      height: '2.25rem',
      opacity: 0,
      pointerEvents: 'none',
      transition: 'opacity 180ms ease-out',
      '@media (max-width: 600px)': {
        width: '1.5rem',
        height: '1.5rem',
        inset: '50% -0.75rem auto auto',
      },
    },
    '@media (hover: hover) and (pointer: fine)': {
      '&:hover .button-hero-label': { translate: '-1rem' },
      '&:hover .button-hero-arrow': { opacity: 1 },
    },
  },
};

const heroArrow = (
  <svg
    className="button-hero-arrow"
    aria-hidden
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth={1.75}
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <path d="M18 8L22 12L18 16" />
    <path d="M2 12H22" />
  </svg>
);

/** Constructivist call to action: flat color, square corners, no shadow. */
export function Button(props: ButtonProps) {
  const {
    tone = 'primary',
    size = 'medium',
    fullWidth,
    startIcon,
    endIcon,
    children,
    href,
    type,
    disabled,
    ...rest
  } = props;
  const { className, css, styleId } = useClassName(
    [toneSx[tone], sizeSx[size], fullWidth ? { width: '100%' } : null],
    baseSx,
  );

  const content =
    size === 'hero' ? (
      <span style={{ position: 'relative', display: 'inline-flex', alignItems: 'center' }}>
        <span className="button-hero-label">{children}</span>
        {heroArrow}
      </span>
    ) : (
      children
    );

  return (
    <>
      {styleRule(styleId, css)}
      {href !== undefined ? (
        <a {...rest} href={href} className={className}>
          {startIcon}
          {content}
          {endIcon}
        </a>
      ) : (
        <button
          {...rest}
          type={type ?? 'button'}
          disabled={disabled}
          aria-disabled={disabled || undefined}
          className={className}
        >
          {startIcon}
          {content}
          {endIcon}
        </button>
      )}
    </>
  );
}
